import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import ManufacturingLineCard from 'components/layout_and_quote_builder/ManufacturingLineCard.jsx';
import LayoutNavSectionCombinationListList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationListList.jsx';
import { setInCache } from 'components/layout_and_quote_builder/utils/SolutionScoreCache.js';
import {
  fetchJsonCached, hasCachedResponse, getCachedResponse, setCachedResponse,
} from 'components/frank/utils/ManufacturingLineResponseCache.js';
import { ProgressProvider, useProgress } from 'components/layout_and_quote_builder/utils/ProgressContext.jsx';
import { DebugPanel } from 'components/layout_and_quote_builder/DebugPopup.jsx';
// The listing's loading state is a modal, blocking popup -- a full-screen card with a short "Loading…" title and
// a determinate percentage bar while the manufacturing lines are scored. It reads more clearly than an inline
// bar. ListingProgressOverlay is Frank's own (it reuses the legacy overlay's styles but with the shorter title),
// so the legacy page's ProgressBarOverlay stays exactly as it is. The solve keeps Frank's own in-place skeleton
// (SolutionCardsSkeleton), whose single request has no percentage to show.
import ListingProgressOverlay from 'components/frank/ListingProgressOverlay.jsx';
import SolutionCardsSkeleton from 'components/frank/SolutionCardsSkeleton.jsx';
import SelectedSolutionSections from 'components/frank/SelectedSolutionSections.jsx';
// The shared de-select: it posts to the reset endpoint, clears the section server-side and tells the linked
// sections. A JS helper, not a Ruby service -- the same reuse the legacy cards get.
import resetSelectedListHandler from 'components/layout_and_quote_builder/quote_builder/resetSelectedListHandler.js';
import skeletonStyles from 'components/frank/_style.module.scss';
import { defaultRequestQueueManager } from 'components/layout_and_quote_builder/utils/RequestQueueManager.js';
import {
  readSelectedManufacturingLineList,
  writeSelectedManufacturingLineList,
  clearSelectedManufacturingLineList,
} from 'components/frank/utils/manufacturingLineListUrlState.js';

// The stable address of ONE distinct manufacturing line: (sub_section_index, identifier). The same line is
// reused across many lists, so its best score is fetched once under this key. See #bestScoreByLine.
const lineKey = (line) => `${line.sub_section_index}:${line.identifier}`;

// The star rating for a score: BEST (lowest) = 5 stars, worst = 1, by the score's position in the RANGE of the
// scores on screen -- the same shape the solution listing uses (sortSolutionsByScoreAndDrawStars). null for a
// plan with no score; 5 when there is only one score or they are all equal (all equally best).
const starRatingForScore = (score, minScore, maxScore) => {
  if (score == null) { return null; }

  const range = maxScore - minScore;
  if (!(range > 0)) { return 5; }

  const normalized = (score - minScore) / range;
  return Math.round(Math.max(1, Math.min(5, 5 - (normalized * 4))) * 10) / 10; // 5 down to 1, never 0
};

// A star row for a rating -- full stars, a half star, empty stars, then the numeric rating. The same rendering
// the legacy solution listing draws, in React.
const Stars = ({ rating }) => {
  if (rating == null) { return null; }

  const full = Math.floor(rating);
  const remainder = rating - full;
  const hasHalf = remainder >= 0.25 && remainder < 0.75;
  const empty = Math.max(0, 5 - full - (hasHalf ? 1 : 0));

  return (
    <span style={{ color: '#ffc107', fontSize: '14px' }}>
      {'★'.repeat(full)}
      {hasHalf ? (
        <span style={{ position: 'relative', display: 'inline-block' }}>
          <span style={{ color: '#ddd' }}>★</span>
          <span style={{ position: 'absolute', left: 0, width: '50%', overflow: 'hidden', color: '#ffc107' }}>★</span>
        </span>
      ) : null}
      {'☆'.repeat(empty)}
      <span style={{ marginLeft: '4px', color: '#666', fontSize: '13px' }}>{rating.toFixed(1)}</span>
    </span>
  );
};

// How many of each part's solutions survive into the combinations. Copied from the legacy listing: without a
// cap, a plan whose two parts have 7 solutions each would list 49 cards, and the 40th-best pairing is not a
// thing anyone reads.
const TOP_N_PER_PART = window.topNPerPart || 3;

// The area-ratio cap (a PERCENT: "20" == a printing bed up to 20% larger in area than the sheet is kept) that
// the /mcc Debug input starts on. Seeded from the server so it matches the applied default: the server reads
// the frank_machine_area_ratio_percent SystemParam (admin-tunable from the database, no deploy), which itself
// falls back to the filter's shipped default -- no second hardcoded value to drift. The literal fallback here
// is only for surfaces that do not set the global, e.g. JS tests.
const DEFAULT_AREA_RATIO_PERCENT = window.frank_machine_area_ratio_percent ?? 20;

// Every way to pick one item from each array. Copied from the legacy listing's cartesianProduct.
const cartesianProduct = (arrays) => arrays.reduce((accumulator, currentArray) => {
  const result = [];
  accumulator.forEach((existingCombination) => {
    currentArray.forEach((item) => { result.push([...existingCombination, item]); });
  });
  return result;
}, [[]]);

// BEST FIRST, AND HOW MANY STARS -- copied from the legacy listing's own sortDivs
// (LayoutNavSectionCombinationList), so a solution ranks and reads identically on both pages.
//
// The stars are NOT a property of one solution: they are its score's position in the RANGE of the scores on
// screen (best = 5, worst = 1), so they can only be worked out once every card has answered. The legacy does it
// in the DOM rather than in React state for exactly that reason -- each card fetches its own score, and
// data-score appears on the grid item as each lands. Frank keeps the same shape, and takes only the two halves
// that are Frank's: the SORT and the STARS. The legacy's other two (the missing-machine button, the show-more
// limit) are its listing's own controls, which Frank does not have.
//
// LOWER IS BETTER: the score is a cost.
const sortSolutionsByScoreAndDrawStars = (grid) => {
  if (!grid) { return; }

  const items = Array.from(grid.children);
  if (items.length === 0) { return; }

  const scoreOf = (item) => parseInt(item.getAttribute('data-score'), 10);

  const sorted = [...items].sort((a, b) => scoreOf(a) - scoreOf(b));
  const alreadySorted = sorted.every((item, index) => item === items[index]);
  if (!alreadySorted) {
    sorted.forEach((item) => grid.appendChild(item));
  }

  // A score of 0 is "not answered yet", not "free" -- the legacy filters those out of the range, or an
  // unanswered card would clamp to a spurious 5.0 and sort to the front.
  const scores = items.map(scoreOf).filter((score) => !Number.isNaN(score) && score > 0);
  if (scores.length === 0) { return; }

  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  const range = maxScore - minScore;
  if (range <= 0) { return; }

  items.forEach((item) => {
    const score = scoreOf(item);
    if (Number.isNaN(score)) { return; }

    const normalizedScore = (score - minScore) / range;
    const starRating = 5 - (normalizedScore * 4); // 5 down to 1, never 0
    const rounded = Math.round(Math.max(1, Math.min(5, starRating)) * 10) / 10;

    let container = item.querySelector('.stars-rating-container');
    if (!container) {
      container = document.createElement('span');
      container.className = 'stars-rating-container';
      item.prepend(container);
    }

    const fullStars = Math.floor(rounded);
    const remainder = rounded - fullStars;
    const hasHalfStar = remainder >= 0.25 && remainder < 0.75;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    let starsHtml = '<span style="color: #ffc107; font-size: 14px; margin-left: 8px;">';
    starsHtml += '★'.repeat(fullStars);
    if (hasHalfStar) {
      starsHtml += '<span style="position: relative; display: inline-block;">'
        + '<span style="color: #ddd;">★</span>'
        + '<span style="position: absolute; left: 0; width: 50%; overflow: hidden; color: #ffc107;">★</span>'
        + '</span>';
    }
    starsHtml += '☆'.repeat(Math.max(0, emptyStars));
    starsHtml += ` <span style="margin-left: 4px; color: #666; font-size: 13px;">${rounded.toFixed(1)}</span></span>`;

    container.innerHTML = starsHtml;
  });
};

// The label each filter's toggle shows, by the key the server sends. Reuses the legacy listing's own label
// keys, so a filter reads the same on both pages.
const FILTER_LABEL_KEYS = {
  smallest_die_cutting_machine_of_each_type: 'smallest_die_cutting_filter',
};

// Each machine-type filter's row heading, and how to label one of its ticks -- both the legacy listing's own
// keys, so the rows read identically on both pages.
const MACHINE_TYPE_FILTER_LABELS = {
  printing_machine_types: {
    headingKey: 'quote_builder.printing_machines',
    optionKey: (type) => `quote_builder.${type}`,
  },
  die_cutting_machine_types: {
    headingKey: 'quote_builder.die_cutting_machines',
    optionKey: (type) => `quote_builder.die_cutting_type_${type}`,
  },
};

// Frank lists a section's SubSectionManufacturingLines on /mcc, below the "Chosen material dimensions": per
// sub_section, the manufacturing lines that can make it, rendered through the SAME cards the legacy page uses
// (ManufacturingLineCard) -- so a manufacturing line reads identically on both pages.
//
// It lists SUB_SECTION manufacturing lines, not ManufacturingLineLists: a ManufacturingLineList is one
// complete way to manufacture the WHOLE section, which for overlapping sub_sections (Cover, Tray, and the
// combined "Tray & Cover" all describing the same parts) is an exact-cover across them. That comes later.
//
// What Frank does not have yet, and so does not render:
//   - "Material dimensions:" chips -- a Frank ManufacturingLine carries its members, not the material sheets
//     (the sheets are the finder's fit input). The card drops that block on its own when the key is absent.
//   - "Priced solutions" -- no Solution Finder yet, so the cards are told there is no count to wait for
//     (hidePricedSolutionCount) rather than being left dimmed behind a spinner forever.
//   - selection -- this is a read-only verify view while Frank grows, so the cards are not clickable.
//
// Admin-gated like the material-sheets line above it.
// The solution this section is already priced from, if any -- read back from the sections the page was
// rendered with (user_quote_nav_sections[].selected_lists), so a refresh or a shared link reopens on the
// priced section rather than on the solution list. It is stored per section, which is why it is looked up by
// sort_order rather than by position.
const restoreSelectedSolutions = (navSectionIndex) => {
  const sections = window.user_quote_nav_sections || [];
  const section = sections.find((candidate) => candidate.sort_order === navSectionIndex);
  const stored = section && section.selected_lists;

  return stored && stored.length > 0 ? stored : null;
};

// IS A SOLUTION CHOSEN? Asked of the LENGTH, never of truthiness, because the reset path sets the selection to
// an EMPTY ARRAY rather than to null -- `resetSelectedListHandler` does `setSelectedList(() => [])`, and the
// legacy section's own Back button (rendered inside QuoteBuilderNavSection, which is handed this setter) goes
// through it. `[]` is truthy in JavaScript, so a truthiness check kept the priced view on screen with nothing
// in it: the reset had already reached the server, and the page showed one orphaned button.
const hasSelection = (selectedSolutions) => Array.isArray(selectedSolutions) && selectedSolutions.length > 0;

// The card flex-basis the wizard uses -- 3 across (33.333%) instead of the page's 4 (25%), because the modal
// is narrower than the full page and four line/solution cards (each two sub-cards wide) come out cramped.
const WIZARD_CARD_BASIS = 'calc(33.333% - 16px)';

// The scoring progress shown INLINE in the wizard's line stage while the manufacturing lines are still being
// scored -- so the wizard shows a determinate bar (not a page of blank-scored cards), and the cards appear only
// once every line has answered. On the normal page the full-screen ListingProgressOverlay does this instead;
// inside the wizard that overlay would sit behind the modal, so this reads the SAME useProgress() data and
// renders in-flow. Rendered inside ManufacturingLineLists' ProgressProvider, so useProgress() is available.
const WizardScoringProgress = () => {
  const { progress } = useProgress();
  const percentage = progress.total > 0 ? progress.percentage : 0;

  return (
    <div style={{ padding: '48px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '14px', color: 'var(--ink-soft, #4e5865)', marginBottom: '14px' }}>
        {t('setup_wizard.scoring')}
      </div>
      <div style={{ maxWidth: '360px', margin: '0 auto', height: '8px', background: 'var(--surface-muted, #f1f3f5)', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${percentage}%`, background: 'var(--brand, #eb5424)', transition: 'width 200ms ease' }} />
      </div>
      <div style={{ fontSize: '12px', color: 'var(--ink-faint, #8a94a3)', marginTop: '8px' }}>{percentage}%</div>
    </div>
  );
};

// The uuids of a solution group, joined -- the stable identity of "this card", used to tell whether two
// groups are the same solution (e.g. a restored selection vs a freshly fetched grid). score_data is dropped
// on persistence, but the uuids survive, so this matches across a remount.
const groupUuids = (group) => (group || []).map((solution) => solution.diecutting_result.uuid).join('|');

// Append the quantity (count) to a fetch's query. The client sends count on all three /mcc fetches so the
// server scores / solves against the count the page is SHOWING -- not whatever user_quote.count happens to be,
// which the count field persists ASYNCHRONOUSLY (a server that read the quote could score a not-yet-saved
// quantity). It also puts count in the URL, so it is part of the response-cache key. Absent count (the legacy
// stacked view, which never passes it) is simply not appended, and the server falls back to the quote's count.
const setCountParam = (query, count) => {
  if (count !== undefined && count !== null) { query.set('count', count); }
};

// stageView: when set, this component renders ONLY ONE sub-view -- 'line' (the filter panel + the
// manufacturing-line listing), 'solution' (the picked plan's solution cards) or 'pricing' (the priced view). It
// is set by BOTH the guided setup wizard (FrankSetupWizard, which splits the flow into modal stages) and the
// section's three page tabs (FrankQuoteBuilderPage: "<Section> - Manufacturing Line / Solution / Pricing"), so
// both surfaces reuse this one component's fetching, scoring, solving and pricing. Absent (the legacy stacked
// admin view) leaves every behaviour unchanged.
//   inWizard distinguishes the two: in the wizard a solution click just MARKS the card (the wizard commits on
//   its own "Done") and cards are drawn 3-across for the narrow modal; on the page tabs a solution is marked
//   and then committed by the Pricing tab, and cards keep the page's 4-across layout.
//   onWizardPickedChange / onWizardSelectionChange report the marked plan / solution up to the wizard (for its
//   Next / Done). onLinePicked / onSolutionPicked fire on a USER click (not on restore) so the page tabs can
//   auto-advance to the next tab.
const ManufacturingLineLists = ({
  navSectionIndex, bobbin, config, stageView, inWizard, count,
  onWizardPickedChange, onWizardSelectionChange, onLinePicked, onSolutionPicked
}) => {
  // The RAW bobbin value (undefined until touched -> the server's part_type default) that the listing is found
  // over -- read off the one `bobbin` descriptor rather than as a second prop the parent has to keep in step
  // with bobbin.checked. Absent (no bobbin row on this page) reads as undefined, exactly as before.
  const bobbinEnabled = bobbin ? bobbin.enabled : undefined;
  const [listing, setListing] = useState(null);
  // Once the debounced re-list actually STARTS, every filter locks -- disabled and dimmed -- until the new
  // listing lands, so a viewer cannot fire a fresh find + score while one is already running. It stays UNlocked
  // during the debounce window (the ticks are cheap, local state), and clears the moment the new listing lands.
  const [isRefetchingListing, setIsRefetchingListing] = useState(false);
  // The reductions switched OFF for the SECTION: ['smallest_die_cutting_machine_of_each_type'].
  // Every one is ON by default -- switching one off is an administrator asking to SEE what it hides, so the
  // reduction is never a silent one.
  const [disabledFilters, setDisabledFilters] = useState([]);
  // The machine types ticked for the SECTION, by filter key: { printing_machine_types: ['offset'] }.
  // Absent means "not spoken yet" -- the server applies the filter's own default (Offset for printing;
  // everything but Digital for die-cutting), so the defaults live in ONE place rather than being duplicated here.
  const [selectedTypes, setSelectedTypes] = useState({});

  // DEBOUNCE. The checkboxes above change the edited values at once so the ticks respond instantly -- but
  // re-listing on every tick would fire a find + score per click. So the LISTING works over the COMMITTED
  // filters, which only catch up to the edited ones once the user has stopped ticking for the debounce
  // (window.frankFilterDebounceMilliseconds, default 1000): tick a few boxes, and 1s after the last one the
  // whole section re-lists ONCE. A section switch commits at once (below), and toggling a box back to the
  // committed value inside the window cancels the pending re-list. `pendingFilterKey` is a stable string of the
  // edited filters; the listing fetch and the score/selection reset both key off `committedFilterKey`, so
  // nothing heavy runs until the delay elapses.
  //   NOTE: the bobbin is the ONE exception. Its material-sheet recompute (the "Chosen material dimensions"
  //   line) fires straight away from the parent's onChange -- only the LISTING half of a bobbin change is
  //   debounced here. See LayoutAndQuoteBuilderNavSection#fetchDefaultMaterialValues.
  //   The area-ratio input (below) is NOT on this debounce either -- it commits on blur / Enter, its own coarse
  //   commit, and drives the fetches directly as areaRatioPercent.
  const pendingFilterKey = JSON.stringify({ bobbinEnabled, disabledFilters, selectedTypes });
  const [committedFilterKey, setCommittedFilterKey] = useState(pendingFilterKey);
  const committedSectionRef = useRef(navSectionIndex);
  // The area-ratio cap the admin is experimenting with, as a percent. areaRatioPercent is the COMMITTED value
  // that drives the fetches (the listing, the solve and the scoring all reduce against it, so a plan is always
  // listed and solved against the same cap); areaRatioInput is the text shown while typing, committed on blur /
  // Enter -- not per keystroke, because each change re-runs a full listing and its heavy per-line scoring.
  const [areaRatioPercent, setAreaRatioPercent] = useState(DEFAULT_AREA_RATIO_PERCENT);
  const [areaRatioInput, setAreaRatioInput] = useState(String(DEFAULT_AREA_RATIO_PERCENT));
  // The ManufacturingLineList the viewer picked, by identifier -- the plan whose solutions are shown. null = the
  // listing, nothing picked.
  // Restored from the address bar up front (manufacturing_line_<index>) so a refresh / shared link reopens the
  // picked plan straight away, before the listing has even loaded. A stale value (a plan the listing that lands
  // does not hold) is dropped once the listing is known -- see the stale-restore guard below.
  const [pickedList, setPickedList] = useState(() => readSelectedManufacturingLineList(navSectionIndex));
  const [solutionsLoading, setSolutionsLoading] = useState(false);
  // The picked plan's SOLUTION GROUPS: each is an ARRAY holding ONE solution per part, and is drawn as ONE
  // card. See #buildSolutionGroups.
  const [solutionGroups, setSolutionGroups] = useState(null);
  // The solutions grid, so the ranking pass can reach the cards' data-score as each lands.
  const solutionsGridRef = useRef(null);
  // The BEST (lowest) score of each DISTINCT manufacturing line, keyed by lineKey -- what the cards sort and
  // label by. Empty until all the per-line best-score requests have landed (the progress bar covers the wait).
  // null for a line that produces no solution (its list sorts last).
  const [bestScoreByLine, setBestScoreByLine] = useState({});
  // THE CHOSEN SOLUTION -- an ARRAY holding one solution per part (a solution GROUP, which is what one card
  // is), or null while none is chosen. Choosing one is what moves the section from "how would this be made" to
  // "what does it cost": it is stored on the section and the cost sections are priced from it.
  //
  // Held WITH THE SECTION IT BELONGS TO, and read back only for that section. The page switches section
  // WITHOUT remounting this component, and a child's effect runs before its parent's -- so a bare array would
  // be visible to children for one render under the NEW index while still holding the PREVIOUS section's
  // choice, and SelectedSolutionSections would post it there. That is not a wasted request: the endpoint
  // writes `lists` straight over the section's selected_lists, so section 0's solution would be saved onto
  // section 1 while the page, one effect later, correctly shows section 1 as unpriced.
  //
  // Restored from what the page was rendered with, so a refresh reopens on the priced section.
  const [selection, setSelection] = useState(() => ({
    index: navSectionIndex,
    solutions: restoreSelectedSolutions(navSectionIndex)
  }));

  // THE PRICED PLAN'S CARD, recovered for the refresh path. On a refresh the listing is re-fetched with the
  // DEFAULT machine-type filters, which drop a plan whose machines are off by default (a Flatbed UV press + a
  // Digital cutter) -- so the priced view can no longer find its own plan in `listing`. It fetches that plan by
  // identifier from the UNFILTERED priced endpoint into here. null while nothing is priced, or when the listing
  // already holds the plan (a fresh in-session select, where the filters the viewer ticked to surface it are
  // still set).
  const [pricedPlanData, setPricedPlanData] = useState(null);

  // The solution card the user has MARKED in a 'solution' view (wizard stage or the page's Solution tab). Held
  // separately from `selection` on purpose: setting `selection` would switch this component to the priced view
  // (and its solutions-fetch guard would clear the grid), whereas we want the solution grid to stay on screen
  // with the marked card highlighted. The wizard commits it on its own "Done"; the page's Pricing tab commits
  // it (see the pricing commit effect). Restored from the already-priced section on mount so revisiting a
  // completed section re-marks its card.
  const [markedSolution, setMarkedSolution] = useState(
    () => (stageView === 'solution' ? restoreSelectedSolutions(navSectionIndex) : null)
  );

  const selectedSolutions = selection.index === navSectionIndex ? selection.solutions : null;

  // The setter the children are handed. They pass an array or an updater, exactly as they would to a plain
  // useState setter (the shared resetSelectedListHandler passes `() => []`); filing it against the section on
  // screen is this component's business, not theirs.
  const setSelectedSolutions = useCallback((value) => {
    setSelection((previous) => {
      const current = previous.index === navSectionIndex ? previous.solutions : null;
      return {
        index: navSectionIndex,
        solutions: typeof value === 'function' ? value(current) : value
      };
    });
  }, [navSectionIndex]);

  // Mark a solution card (does NOT set `selection`, see #markedSolution). Clicking the marked card again
  // unmarks it. Used by both the wizard solution stage and the page's Solution tab.
  const markSolution = useCallback((group) => {
    setMarkedSolution((previous) => (groupUuids(previous) === groupUuids(group) ? null : group));
  }, []);

  // Report the marked plan / marked solution up to the wizard (FrankSetupWizard) as they change, so its
  // Next / Done buttons enable and disable in step with the selection on screen. No-ops on the page tabs (the
  // callbacks are absent there).
  useEffect(() => {
    if (onWizardPickedChange) { onWizardPickedChange(pickedList); }
  }, [pickedList, onWizardPickedChange]);

  useEffect(() => {
    if (onWizardSelectionChange) { onWizardSelectionChange(markedSolution); }
  }, [markedSolution, onWizardSelectionChange]);

  // THE PAGE'S PRICING TAB COMMITS THE MARKED SOLUTION. The wizard commits on its Done; on the page there is no
  // Done, so arriving at the Pricing tab is what commits the marked solution -- setting `selection` renders the
  // priced view, which posts select-solution and prices the section. It commits whenever the mark DIFFERS from
  // what is priced, so re-picking a different solution on the Solution tab re-prices too.
  useEffect(() => {
    if (stageView !== 'pricing' || inWizard) { return; }
    if (hasSelection(markedSolution) && groupUuids(markedSolution) !== groupUuids(selectedSolutions)) {
      setSelectedSolutions(markedSolution);
    }
  }, [stageView, inWizard, markedSolution, selectedSolutions, setSelectedSolutions]);

  // THE PLAN THE CHOSEN SOLUTION WAS MADE ON, derived from the chosen CARDS rather than read from `pickedList`.
  // A list's identifier is exactly its manufacturing lines' identifiers in cover order
  // (Frank::ManufacturingLineList#identifier) and every card carries the line it was solved on, so the plan is
  // a property of the selection itself. It has to be: `pickedList` is page state, and the two ways a priced
  // section reaches the screen without one are both normal -- a link straight into a priced section never
  // picked anything, and switching section clears the pick while the selection is restored from the server.
  //
  // ALL OR NOTHING: a card with no identifier (a selection made on the legacy page, which stamps none) makes
  // the whole thing null. Dropping the empty ones instead would turn "I know some of the lines" into a
  // complete-looking identifier -- and on a two-part section that shortened string can MATCH a different,
  // single-line plan in the listing, which would then be drawn as the plan and scoped the scoring.
  const pricedLineIdentifiers = hasSelection(selectedSolutions)
    ? selectedSolutions.map((solution) => solution.diecutting_result.manufacturing_line_identifier)
    : [];
  const pricedListIdentifier = pricedLineIdentifiers.length > 0 && pricedLineIdentifiers.every(Boolean)
    ? pricedLineIdentifiers.join('|')
    : null;

  // On a reopened PRICED page section, solve for the plan the section is ACTUALLY priced on -- not a stale
  // manufacturing_line_<index> left in the URL by an earlier pick. Runs ONCE, so a later in-session pick is left
  // alone. This keeps the Solution tab listing the priced plan's solutions (with the priced one selected) on
  // reload, and the Manufacturing Line tab highlighting the right plan. Not in the wizard, which drives its own
  // pick and deliberately ignores the restored selection.
  const syncedPricedPickRef = useRef(false);
  useEffect(() => {
    // Page tabs only: the legacy stacked view (no stageView) and the wizard (inWizard) keep their own behaviour.
    if (!stageView || inWizard || syncedPricedPickRef.current) { return; }
    if (!hasSelection(selectedSolutions) || !pricedListIdentifier) { return; }
    syncedPricedPickRef.current = true;
    if (pickedList !== pricedListIdentifier) { setPickedList(pricedListIdentifier); }
  }, [stageView, inWizard, selectedSolutions, pricedListIdentifier, pickedList]);

  // THE ONE PLAN THE PAGE IS ABOUT, if any: the picked one while a solution is being chosen, the priced one
  // once it has been. Everything scoped to "the plan on screen" -- which lines get scored, which lists the star
  // ratings normalize against, which card is drawn -- follows this rather than the pick alone. Scoring did not,
  // which is why a priced section opened from a link scored every plan in the listing for a listing it never
  // shows.
  //
  // Once a solution is CHOSEN it is the derived plan or nothing -- never `pickedList`. The pick is whatever
  // manufacturing_line_<index> happens to say in the address bar, which on a deep link into a section priced
  // from a legacy selection is a plan with nothing to do with the chosen solution.
  // In the WIZARD, the "plan on screen" is always the marked pick -- the wizard drives selection through its own
  // markedSolution and does the pricing itself on Done, so a `selectedSolutions` restored from an already-priced
  // section (a reopened quote) must NOT take over here: doing so scoped scoring to the old plan and, worse,
  // blocked the solutions fetch (see the solve effect), which is why a freshly picked plan showed "no solutions".
  const planOnScreen = (hasSelection(selectedSolutions) && !stageView) ? pricedListIdentifier : pickedList;

  // THE SOLUTION GROUPS -- what a card actually is.
  //
  // A ManufacturingLineList makes the WHOLE section, and it can do that with more than one manufacturing line:
  // a shirt box's plan is "Kapak on the KBA + BOBST, Kutu on the 70x100 Pedal". Those two are not two answers,
  // they are two HALVES of one answer -- so one card must show a solution for EACH part, and the card is the
  // pair. Listing each part's solutions on their own says "here are some covers and some trays" and leaves the
  // reader to do the pairing.
  //
  // So the groups are the CARTESIAN PRODUCT across the plan's parts, copied from the legacy listing's
  // handlePartsMode:
  //     one part  -> [[s1], [s2], ...]                 (a group of one; every solution is already whole)
  //     two parts -> [[kapak1, kutu1], [kapak1, kutu2], ...]
  //
  // The product is taken over the BEST TOP_N_PER_PART of each part. Best needs the score -- which now arrives
  // WITH the solution (score_data, computed server-side by Frank::SolutionScore), so this is a plain
  // SYNCHRONOUS sort, no per-solution round trip.
  //
  // WARMING THE CACHE (not an XHR): the reused legacy solution card reads its score from the shared score cache
  // (LayoutNavSectionCombination#getScore checks the cache before fetching). Priming that cache from the
  // embedded score_data is what lets Frank reuse the legacy card WITHOUT its per-card fetch to
  // /layouts-nav-section-combination-score -- the card finds every score already answered.
  //
  // A part with NO solutions collapses the whole product to nothing, correctly: if the Kutu cannot be made on
  // its assigned machine, the PLAN cannot make the section, however many ways the Kapak has.
  const buildSolutionGroups = (data) => {
    const groups = (data && data.solutions) || [];

    // Prime the score cache from the embedded score_data, so the legacy card renders every solution with no XHR.
    groups.forEach((group) => group.solutions.forEach((solution) => {
      if (solution.score_data) { setInCache(solution.diecutting_result.uuid, solution.score_data); }
    }));

    const perPart = groups.map((group) => group.solutions).filter((list) => list.length > 0);

    // Every part must be makeable, or the plan makes nothing.
    if (perPart.length === 0 || perPart.length !== groups.length) { return []; }

    // One part: each solution is already a whole way to make the section.
    if (perPart.length === 1) { return perPart[0].map((solution) => [solution]); }

    // lower is better: the score is a cost. A missing score sorts last.
    const scoreOf = (solution) => (solution.score_data ? solution.score_data.score : Infinity);
    const bestPerPart = perPart.map((list) => [...list]
      .sort((a, b) => scoreOf(a) - scoreOf(b))
      .slice(0, TOP_N_PER_PART));

    return cartesianProduct(bestPerPart);
  };

  const toggleFilter = (key) => setDisabledFilters((previous) => (
    previous.includes(key) ? previous.filter((each) => each !== key) : [...previous, key]
  ));

  // Tick or untick one machine type. The CURRENT selection comes from the server (it applied the defaults), so
  // the first tick starts from what is actually on screen rather than from an empty set.
  const toggleMachineType = (key, type, currentlySelected) => setSelectedTypes((previous) => {
    const selected = previous[key] || currentlySelected;
    const next = selected.includes(type) ? selected.filter((each) => each !== type) : [...selected, type];
    return { ...previous, [key]: next };
  });

  // Commit the edited filters to `committedFilterKey` 2s after the last edit, so a burst of ticks becomes ONE
  // re-list. A SECTION switch commits at once (its listing must not wait), and an edit back to the committed
  // value cancels the pending commit (the cleanup clears the timer before the next run sees them equal).
  useEffect(() => {
    const sectionChanged = committedSectionRef.current !== navSectionIndex;
    committedSectionRef.current = navSectionIndex;
    if (sectionChanged) { setCommittedFilterKey(pendingFilterKey); return; }
    if (pendingFilterKey === committedFilterKey) { return; }

    // window.frankFilterDebounceMilliseconds lets the tests commit at once (0); the != guards nullish so an
    // explicit 0 is honoured (the same test seam window.topNPerPart uses for the per-part cap).
    const delay = window.frankFilterDebounceMilliseconds != undefined ? window.frankFilterDebounceMilliseconds : 1000;
    const timer = setTimeout(() => setCommittedFilterKey(pendingFilterKey), delay);
    return () => clearTimeout(timer);
    // committedFilterKey is read above; when a commit makes it equal pendingFilterKey the re-run early-returns.
  }, [navSectionIndex, pendingFilterKey, committedFilterKey]);

  // Commit the typed area-ratio percent -- on blur or Enter, so the expensive re-list / re-score runs once the
  // admin has finished a value, not on every keystroke. A blank or non-numeric entry (or a negative, which is
  // not a "percent larger") is rejected: the field snaps back to the committed value and nothing re-fetches. A
  // valid value that has not changed does not re-fetch either -- React bails on the same state.
  const commitAreaRatio = () => {
    const parsed = Number(areaRatioInput);
    if (areaRatioInput.trim() === '' || Number.isNaN(parsed) || parsed < 0) {
      setAreaRatioInput(String(areaRatioPercent));
      return;
    }
    setAreaRatioPercent(parsed);
    setAreaRatioInput(String(parsed));
  };

  // The bobbin checkbox changes the section's material sheets, and the manufacturing lines are found over
  // those sheets -- so re-fetch when the bobbin commits, or the lines shown would be the ones for the previous
  // sheets. A machine-type or reduction toggle re-fetches for the same reason (the reduction happens
  // server-side, on the found lines) -- all three land here through committedFilterKey, after the debounce.
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined) { return; }

    const query = new URLSearchParams();
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    disabledFilters.forEach((key) => query.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      // A query string cannot carry an empty array, and "absent" already means "use the default" -- so an
      // empty selection is sent as one blank entry, which the server reads as "nothing ticked".
      if (types.length === 0) {
        query.append(`${key}[]`, '');
        return;
      }
      types.forEach((type) => query.append(`${key}[]`, type));
    });
    // The admin's area-ratio cap, so the listing is reduced against the same value the solve and scoring use.
    query.set('area_ratio_percent', areaRatioPercent);
    // The listing itself is count-independent (it lists which plans CAN make the section, not their cost), but
    // count is sent so a quantity change re-fetches it and so re-mints the `listing` object the best-score effect
    // keys off -- which is what re-triggers the (count-dependent) scoring. It also keeps the cache key uniform:
    // count rides in the URL on all three fetches. The server ignores it here.
    setCountParam(query, count);
    const queryString = query.toString();

    // superseded guards against out-of-order responses: a slow earlier fetch must not land its (stale) listing
    // after a later one -- which would then drive the best-score batch over sheets it was not listed with. It
    // also guards the lock: only the LATEST fetch clears it, so a superseded early answer cannot unlock the
    // filters while the real re-list is still running.
    let superseded = false;
    setIsRefetchingListing(true);
    // Through the response cache, keyed by this URL (count is in it): an identical listing the wizard (or an
    // earlier visit) just fetched is reused instead of re-fetched. See ManufacturingLineResponseCache.
    fetchJsonCached(`/mcc/${window.user_quote_id}/manufacturing-line-lists/${navSectionIndex}${queryString ? `?${queryString}` : ''}`)
      .then((data) => {
        if (superseded) { return; }
        if (data) { setListing(data); }
        setIsRefetchingListing(false);
      })
      .catch((error) => {
        console.error('Error fetching manufacturing line lists:', error);
        if (!superseded) { setIsRefetchingListing(false); }
      });

    return () => { superseded = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the body reads the LATEST filter values, which by
    // the time committedFilterKey changes ARE the committed ones (a further edit would have restarted the
    // debounce); so committedFilterKey is the trigger, not bobbinEnabled/disabledFilters/selectedTypes directly.
    // areaRatioPercent is its own trigger (committed on blur / Enter, not on the checkbox debounce). count is a
    // trigger too: a quantity change re-fetches the listing, which re-mints the `listing` object the scoring
    // effect keys off, so the (count-dependent) best scores are recomputed for the new quantity.
  }, [navSectionIndex, committedFilterKey, areaRatioPercent, count]);

  // The picked plan's SOLUTIONS: how this plan would actually make the section. Fetched only once a plan is
  // picked, because the solve is the expensive half -- the listing above answers without it, which is why the
  // cards can appear long before any solution does.
  //
  // The same inputs the listing was fetched with are sent, so the plan is solved with the very sheets and
  // filters it was listed with: a plan identifier only means anything against a given listing.
  useEffect(() => {
    // The pricing view does not show the solutions grid, so there is nothing to solve there -- and re-solving
    // when a selection lands would be wasted work. Keep whatever groups were already fetched.
    if (stageView === 'pricing') { return; }
    // No plan picked, or a solution already CHOSEN: either way there is nothing to solve. Reopening a priced
    // section still had its plan in the address bar, so it fired a full solve whose answer was then never
    // rendered -- frank.contract calls the solve the expensive half of the page.
    //   In a 'line'/'solution' view, selection runs through markedSolution, not selectedSolutions, so a
    //   `selectedSolutions` restored from an already-priced section must NOT block the solve -- otherwise a
    //   plan the user just picked shows "no solutions" because the solve never ran.
    const chosenBlocksSolve = hasSelection(selectedSolutions) && !stageView;
    if (!window.frank_mode || navSectionIndex === undefined || !pickedList || chosenBlocksSolve) {
      setSolutionGroups(null);
      return;
    }

    const query = new URLSearchParams();
    query.set('manufacturing_line_list', pickedList);
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    disabledFilters.forEach((key) => query.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      if (types.length === 0) {
        query.append(`${key}[]`, '');
        return;
      }
      types.forEach((type) => query.append(`${key}[]`, type));
    });
    // The same cap the listing used -- a plan is only meaningful against the listing it came from.
    query.set('area_ratio_percent', areaRatioPercent);
    // The quantity the server solves/scores against -- see setCountParam.
    setCountParam(query, count);

    let superseded = false;
    setSolutionsLoading(true);
    // Through the response cache (keyed by this URL, count included), so the plan the wizard just solved is not
    // solved again when the tab mounts. The raw solve response is cached; buildSolutionGroups still runs each
    // time (it is a cheap local transform, not the expensive solve).
    fetchJsonCached(`/mcc/${window.user_quote_id}/solutions/${navSectionIndex}?${query.toString()}`)
      .then((data) => buildSolutionGroups(data))
      .then((groups) => {
        if (superseded) { return; }
        setSolutionGroups(groups);
        setSolutionsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching solutions:', error);
        if (superseded) { return; }
        setSolutionGroups([]);
        setSolutionsLoading(false);
      });

    return () => { superseded = true; };
    // selectedSolutions is a dependency because the guard above READS it: going back from a priced section has
    // to solve the plan again, and choosing one has to stop solving. count is a dependency because it is sent in
    // the solve URL and changes the solve's scores, so a quantity change must re-solve.
  }, [pickedList, navSectionIndex, bobbinEnabled, disabledFilters, selectedTypes, areaRatioPercent, selectedSolutions, count]);

  // BEST SCORES -- so a card can sort best-first and show its score. Scoring is heavy (every solution of every
  // line, min per line, no pruning: the cheapest way is not guessable from the layout), so WHERE it runs matters:
  //   - LISTING on screen (no plan picked): score EVERY distinct line, ONCE per listing, so the cards can sort
  //     and star. Many small per-line requests through defaultRequestQueueManager -- each well under the 15s
  //     request timeout -- with ListingProgressOverlay's modal bar over completed/total.
  //   - ONE plan on screen -- picked, or the plan a chosen solution is priced from (a click, or a refresh /
  //     deep link straight into either view): score ONLY that plan's lines. That card needs just its OWN best
  //     score; scoring all ~42 lines there is what made a deep link slow and ran the full progress bar for
  //     nothing. The rest are scored if and when the user goes Back to the listing.
  // A line is fetched ONCE (it is reused across many lists); a list's best is the SUM of its lines' bests.
  //
  // `fullyScoredListingRef` remembers the listing object we have already scored in full, so neither picking a
  // plan from a scored listing nor going Back to it re-scores (the scores are kept) -- only a NEW listing (a
  // filter / section change) or a Back from a deep link (never fully scored) triggers the full pass.
  const fullyScoredListingRef = useRef(null);
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined || !listing) { return undefined; }
    if (fullyScoredListingRef.current === listing) { return undefined; }

    const lists = listing.manufacturing_line_lists || [];
    // In the WIZARD's line stage the user compares EVERY manufacturing line, so all of them are scored -- a pick
    // restored from the URL must not narrow scoring to itself and leave the other cards score-less. Elsewhere
    // (and once a solution is priced) scoring stays scoped to the one plan on screen.
    const planScope = stageView === 'line' ? null : planOnScreen;
    // NOT "the picked plan": it may equally be the plan a chosen solution is priced from, which is no pick at
    // all. What it always is, is the one plan on screen.
    const planOnScreenList = planScope ? lists.find((list) => list.identifier === planScope) : null;
    // An identifier the listing does not hold (a stale deep link) is dropped by the stale-restore guard; there
    // is nothing to score until it resolves to the listing or a real plan.
    if (planScope && !planOnScreenList) { return undefined; }

    // One plan -> just its lines (merged in, keeping any we already have). Listing -> every line, a fresh
    // full score; on COMPLETION it marks this listing done (below) so picking / Back does not repeat it.
    const scoresWholeListing = !planOnScreenList;
    const scoredLists = planOnScreenList ? [planOnScreenList] : lists;

    const distinctLines = [];
    const seen = new Set();
    scoredLists.forEach((list) => (list.manufacturing_lines || []).forEach((line) => {
      if (line.sub_section_index == null) { return; }
      const key = lineKey(line);
      if (!seen.has(key)) { seen.add(key); distinctLines.push(line); }
    }));
    if (distinctLines.length === 0) { return undefined; }

    // The SAME filter/bobbin params the listing used, so each line is scored over the very sheets it was listed
    // with.
    const filterParams = new URLSearchParams();
    if (bobbinEnabled !== undefined) { filterParams.set('bobbin_enabled', bobbinEnabled); }
    disabledFilters.forEach((key) => filterParams.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      if (types.length === 0) { filterParams.append(`${key}[]`, ''); return; }
      types.forEach((type) => filterParams.append(`${key}[]`, type));
    });
    filterParams.set('area_ratio_percent', areaRatioPercent);
    // The quantity the server scores against -- see setCountParam. Shared into every per-line URL below.
    setCountParam(filterParams, count);

    let superseded = false;
    // Cancel a superseded batch's requests. An aborted signal makes an in-flight fetch reject AND makes a
    // still-QUEUED fetch reject immediately when the queue reaches it -- WITHOUT sending the request -- so a
    // rapid filter toggle does not pile N x 42 heavy solves on the server (each request runs a full solve).
    const controller = new AbortController();
    if (scoresWholeListing) { setBestScoreByLine({}); }
    defaultRequestQueueManager.reset();

    Promise.all(distinctLines.map((line) => {
      const query = new URLSearchParams(filterParams);
      query.set('manufacturing_line', line.identifier);
      query.set('sub_section_index', line.sub_section_index);
      const url = `/mcc/${window.user_quote_id}/manufacturing-line-best-score/${navSectionIndex}?${query.toString()}`;

      // A best score already computed for these exact inputs (e.g. the wizard just scored this very
      // manufacturing line at this quantity -- the quantity is in the url) is reused straight from the response
      // cache -- no request-queue slot, no second server-side solve. Only an uncached manufacturing line goes to
      // the network. The null a non-OK response yields is not cached, so a transient failure does not stick.
      const scoreData = hasCachedResponse(url)
        ? Promise.resolve(getCachedResponse(url))
        : defaultRequestQueueManager.enqueue(() => fetch(url, { signal: controller.signal })
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => { if (data !== null) { setCachedResponse(url, data); } return data; })
          .catch(() => null)); // aborts and network errors both land here

      return scoreData
        .then((data) => ({ key: lineKey(line), best: data ? data.best_score : null }))
        // Written the MOMENT this one line answers, not once the whole batch has: that is what lets each card
        // fill in its own score as it arrives, instead of the listing sitting blank behind a full-screen bar
        // and then appearing all at once. A superseded batch writes nothing.
        //
        // The key is recorded even when the score is null (an unmanufacturable line), because the card has to
        // tell "answered, cannot be made" from "not answered yet" -- they look different: an em dash against a
        // still-loading placeholder.
        .then((result) => {
          if (!superseded) {
            setBestScoreByLine((previous) => ({ ...previous, [result.key]: result.best }));
          }
          return result;
        });
    })).then(() => {
      if (superseded) { return; }
      // Mark the listing fully scored only NOW, once the batch actually landed. Recording it at the START would
      // strand the listing scoreless if a pick interrupted the batch: the aborted run leaves bestScoreByLine {},
      // and the next run would skip on `ref === listing` and never score anything.
      if (scoresWholeListing) { fullyScoredListingRef.current = listing; }
    });

    return () => { superseded = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `listing` is the join point (a filter / bobbin /
    // area-ratio / section / COUNT change mints a new listing before this should re-run, and navSectionIndex is
    // stable per mounted instance), so only `listing` and `planOnScreen` trigger this -- not the filter values
    // (the area-ratio cap among them) or the count the body reads: a count change re-lists above, and the new
    // listing object re-triggers this pass, which then scores against that same count via the cache key.
  }, [listing, planOnScreen]);

  // A filter change re-lists, and the picked plan may no longer exist in the new listing -- so drop the
  // selection rather than show solutions for a plan that is no longer on screen. Clear the best scores in the
  // SAME pass, so the cards never show the previous listing's scores against the new listing for a render (the
  // best-score effect clears them too, but only after the render that first sees the new listing).
  //
  // Skip the very FIRST run: the picked plan is restored from the URL on mount (above), and this clear must not
  // wipe that restore -- only a later filter / section change should drop the selection.
  // Keyed off committedFilterKey (not the edited values), so the current listing's scores and stars stay put
  // while the user is still ticking -- they clear in the SAME pass the re-list fires, once the debounce elapses.
  const selectionResetSkipRef = useRef(true);
  useEffect(() => {
    if (selectionResetSkipRef.current) { selectionResetSkipRef.current = false; return; }
    setPickedList(null);
    setBestScoreByLine({});
    // The chosen solution goes too: it was chosen out of a listing these filters have just replaced, so the
    // plan it was found on may no longer be offered at all.
    //
    // A SECTION change is different from a filter change, though: the new section may already be priced, and
    // its own selection has to come back. This component is NOT remounted when the page switches section --
    // FrankQuoteBuilderPage renders one section at a time and LayoutNavSectionList keys its child by the map
    // POSITION, which is always 0 for a one-element array -- so restoring only in the useState initializer left
    // an already-priced second section (a shirt box's Kapak) showing the solution listing instead.
    setSelectedSolutions(restoreSelectedSolutions(navSectionIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- committedFilterKey is the debounced trigger and
    // areaRatioPercent its own (blur-committed) one; a section switch resets at once and changes both, so the
    // reset stays in step with the re-list the same change fires.
  }, [navSectionIndex, committedFilterKey, areaRatioPercent]);

  // Mirror the picked plan into the address bar so a refresh / shared link reproduces it. The param is removed
  // when the selection goes away -- go back, or a filter change that drops it.
  useEffect(() => {
    if (navSectionIndex === undefined) { return; }
    if (pickedList) {
      writeSelectedManufacturingLineList(navSectionIndex, pickedList);
    } else {
      clearSelectedManufacturingLineList(navSectionIndex);
    }
  }, [pickedList, navSectionIndex]);

  // A plan restored from the URL may name one the current listing does not hold (a shared link after the
  // catalogue or filters changed). Once the listing lands, drop such a stale selection so the page shows the
  // listing rather than an empty picked view.
  useEffect(() => {
    if (!listing || !pickedList) { return; }
    const lists = listing.manufacturing_line_lists || [];
    if (!lists.some((list) => list.identifier === pickedList)) { setPickedList(null); }
  }, [listing]);

  // Rank the solutions once their scores land. Each card fetches its OWN score (the legacy card does this
  // itself), so the scores arrive one at a time and the ranking can only settle after the last one -- which is
  // why the legacy re-runs sortDivs on a 200ms timer rather than once. Frank does the same, but STOPS: it polls
  // only while the solutions are on screen, and the interval is cleared when the picked plan goes away.
  useEffect(() => {
    if (!pickedList) { return undefined; }

    const timer = setInterval(() => sortSolutionsByScoreAndDrawStars(solutionsGridRef.current), 200);
    return () => clearInterval(timer);
  }, [pickedList, solutionGroups]);

  // THE PRICED PLAN, for the refresh path. When a solution is chosen but the (default-filtered) listing does not
  // hold its plan, fetch that plan by identifier from the UNFILTERED priced endpoint so the priced card can still
  // be drawn. This is the refresh case: the component remounts with no ticked machine-type filters, so the
  // listing comes back without a plan whose machines are off by default (a Flatbed UV press + a Digital cutter).
  // On a fresh in-session select the listing already holds the plan (the filters the viewer ticked to surface it
  // are still set), so nothing is fetched.
  //
  // WAITS FOR THE LISTING (does nothing while it is null): the listing loads async, so acting before it arrives
  // would read an empty listing, miss the plan, and fire a recovery fetch on every priced-view load -- even when
  // the listing is about to hold the plan. Once it has loaded, the plan-in-listing check is real.
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined
        || !hasSelection(selectedSolutions) || !planOnScreen || !listing) {
      setPricedPlanData(null);
      return undefined;
    }
    const inListing = (listing.manufacturing_line_lists || []).some((list) => list.identifier === planOnScreen);
    if (inListing) { setPricedPlanData(null); return undefined; }

    const query = new URLSearchParams();
    query.set('manufacturing_line_list', planOnScreen);
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    query.set('area_ratio_percent', areaRatioPercent);

    let superseded = false;
    fetch(`/mcc/${window.user_quote_id}/priced-manufacturing-line-list/${navSectionIndex}?${query.toString()}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => { if (!superseded) { setPricedPlanData(data ? data.manufacturing_line_list : null); } })
      .catch(() => { if (!superseded) { setPricedPlanData(null); } });

    return () => { superseded = true; };
  }, [selectedSolutions, planOnScreen, listing, navSectionIndex, bobbinEnabled, areaRatioPercent]);

  // The normal /mcc page keeps this admin/v2 gate while the listing UX is still being productized. The guided
  // setup wizard, however, is the customer-facing surface (Frank only), so when this component is hosted inside
  // it (stageView set) the gate is relaxed and every Frank user can complete the flow.
  const canSee = stageView != null || window.current_user_admin || window.quote_builder_v2_enabled;
  if (!canSee || !listing) { return null; }

  // The reduction-toggle and area-ratio blocks are ADMIN tuning controls. They live in <DebugPanel>, which
  // portals into the admin debug popup -- but falls back to rendering INLINE when there is no popup (a
  // non-admin). On the normal page that never showed because canSee gated the whole component; in the wizard
  // (which relaxes that gate) they would render inline for customers, so gate them on the admin/v2 flags here.
  const adminDebugVisible = window.current_user_admin || window.quote_builder_v2_enabled;

  // What the page LISTS -- the ONLY thing it lists: every complete way to manufacture the section. The
  // sub_sections are the working behind these and are not listed: "Kapak" on its own makes no shirt box, it
  // makes a cover, and every one of its manufacturing lines already appears inside the lists that use it.
  const manufacturingLineLists = listing.manufacturing_line_lists || [];

  // THE PLANS THE PRICED VIEW REASONS ABOUT: the listing, plus the priced plan recovered for the refresh path
  // when the listing does not hold it (pricedPlanData). The priced card's lookup and the machine scale it is
  // drawn against read this, so a plan the default filters dropped is still drawn to scale. Null pricedPlanData
  // (nothing priced, or the listing already holds the plan) leaves this as just the listing.
  const effectiveManufacturingLineLists = pricedPlanData
    && !manufacturingLineLists.some((list) => list.identifier === pricedPlanData.identifier)
    ? [...manufacturingLineLists, pricedPlanData]
    : manufacturingLineLists;

  // ANSWERED, not "answered with a score": a line whose request came back unmanufacturable is recorded as null,
  // so presence of the KEY is what separates "we know" from "still waiting". Reading the value cannot tell them
  // apart, and the card draws them differently.
  const lineScored = (line) => Object.prototype.hasOwnProperty.call(bestScoreByLine, lineKey(line));
  const listScored = (list) => list.manufacturing_lines.every(lineScored);

  // The plans actually ON SCREEN: every one while the listing shows, just the one plan otherwise (picked, or
  // the plan a chosen solution is priced from). Scoring is scoped the same way (see the best-score effect), so
  // this is also the set that can be expected to answer -- asking whether the plans the viewer cannot see have
  // answered would leave that one card waiting forever. The wizard's line stage lists ALL plans (it scores them
  // all), so a restored pick does not narrow this to itself -- otherwise bestScoresReady would flip true after
  // one card and the rest would show blank scores.
  const listsOnScreen = (planOnScreen && stageView !== 'line')
    ? manufacturingLineLists.filter((list) => list.identifier === planOnScreen)
    : manufacturingLineLists;

  // Whether every plan on screen has answered. Individual cards no longer wait for this -- each shows its own
  // score as it lands -- but the two things that are a property of the SET still do: the best-first sort, and
  // the star ratings, which are a score's position in the range on screen and so cannot be known until the
  // range is complete.
  const bestScoresReady = listsOnScreen.length > 0 && listsOnScreen.every(listScored);

  // A LIST's best = the SUM of its lines' bests (parts are made independently on their own lines). null when any
  // of its lines is unmanufacturable (no solution) -- that list cannot make the section, so it sorts last.
  const listBestScore = (list) => {
    const perLine = list.manufacturing_lines.map((line) => bestScoreByLine[lineKey(line)]);
    if (perLine.some((score) => score == null)) { return null; }

    return perLine.reduce((sum, score) => sum + score, 0);
  };

  // The range of the lists' best scores on screen -- the star ratings normalize against it (best = 5, worst =
  // 1). Unmanufacturable plans (null) are excluded from the range and get no stars.
  const listBestScores = bestScoresReady
    ? listsOnScreen.map(listBestScore).filter((score) => score != null)
    : [];
  const minListScore = listBestScores.length ? Math.min(...listBestScores) : 0;
  const maxListScore = listBestScores.length ? Math.max(...listBestScores) : 0;
  // The filters are the SECTION's: one set, applied to every sub_section the lists are built from.
  const machineTypeFilters = listing.machine_type_filters || [];
  const filters = listing.filters || [];

  // Largest machine sheet dimension across every manufacturing line the page draws -- the common scale that
  // makes the per-machine rectangles comparable to each other (a bigger machine renders bigger), including
  // across the two lines stacked in one list. The same scale the legacy listing computes.
  const allManufacturingLines = effectiveManufacturingLineLists.flatMap((list) => list.manufacturing_lines);
  const maxMachineDimensionMm = Math.max(1, ...allManufacturingLines.flatMap((manufacturingLine) => [
    manufacturingLine.sheet_width_mm, manufacturingLine.sheet_height_mm,
    manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm
  ].map((value) => parseFloat(value) || 0)));

  // The SECTION's filter rows, drawn as ONE aligned card so the bobbin question and the two machine-type rows
  // read as a single control:
  //     Malzeme:                    [x] Bobin
  //     Baskı Makineleri:      [x] Offset  [ ] Dijital ...
  //     Kalıplı kesim makineleri:  [ ] Dijital  [x] Otomatik ...
  // The ticks are instant and local; the re-list they add up to fires 2s after the last one (the debounce), and
  // only THEN does the block dim and its inputs disable (isRefetchingListing) -- see the wrapper below. So the
  // viewer can set a few boxes before anything recalculates. The bobbin row is Frank's: the legacy page draws
  // its own in ChooseLayoutDimensions, and this component is /mcc only.
  const headingStyle = { fontSize: '13px', color: 'var(--ink, #10141a)', fontWeight: 600, whiteSpace: 'nowrap' };
  const optionsRowStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' };
  const optionLabelStyle = {
    fontSize: '13px', color: 'var(--ink-soft, #4e5865)', fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer'
  };
  const checkboxStyle = { accentColor: 'var(--brand, #eb5424)', width: '15px', height: '15px', margin: 0, cursor: 'pointer' };

  const renderSectionFilters = () => {
    const bobbinRowShown = bobbin && bobbin.shown;
    if (!bobbinRowShown && machineTypeFilters.length === 0) { return null; }

    return (
      <div
        style={{
          display: 'inline-grid', gridTemplateColumns: 'max-content 1fr', gap: '10px 20px', alignItems: 'center',
          padding: '12px 18px', textAlign: 'left',
          background: 'var(--surface, #ffffff)', border: '1px solid var(--rule, #e3e7ec)',
          borderRadius: 'var(--dct-radius, 8px)', boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)'
        }}
      >
        {bobbinRowShown && (
          <React.Fragment>
            <span style={headingStyle}>{t('quote_builder.material')}:</span>
            <div data-section-bobbin-filter style={optionsRowStyle}>
              <label style={{ ...optionLabelStyle, cursor: bobbin.forced ? 'default' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={bobbin.checked}
                  disabled={bobbin.forced || isRefetchingListing}
                  data-bobbin-checkbox
                  onChange={(event) => bobbin.onChange(event.target.checked)}
                  style={{ ...checkboxStyle, cursor: bobbin.forced ? 'default' : 'pointer' }}
                />
                {t('quote_builder.bobbin_label')}
              </label>
            </div>
          </React.Fragment>
        )}

        {machineTypeFilters.map((filter) => {
          const labels = MACHINE_TYPE_FILTER_LABELS[filter.key];
          if (!labels) { return null; }

          const currentlySelected = filter.options.filter((option) => option.selected).map((option) => option.value);
          // The EDITED selection (the ticks), not the server echo -- so a tick shows at once during the debounce
          // window, before the re-list that will confirm it. Absent means "not spoken yet" -> the server's default.
          const editedSelected = selectedTypes[filter.key] || currentlySelected;

          return (
            <React.Fragment key={filter.key}>
              <span style={headingStyle}>{t(labels.headingKey)}:</span>
              <div data-machine-type-filter={filter.key} style={optionsRowStyle}>
                {filter.options.map((option) => (
                  <label key={option.value} style={optionLabelStyle}>
                    <input
                      type="checkbox"
                      checked={editedSelected.includes(option.value)}
                      disabled={isRefetchingListing}
                      data-machine-type={option.value}
                      onChange={() => toggleMachineType(filter.key, option.value, currentlySelected)}
                      style={checkboxStyle}
                    />
                    {t(labels.optionKey(option.value))}
                  </label>
                ))}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // The SECTION's reduction toggles. Each reduces every sub_section's manufacturing lines; unticking one
  // re-fetches and the manufacturing lines it was hiding reappear.
  const renderFilterToggles = () => (
    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '16px', marginTop: '8px' }}>
      {filters.map((filter) => (
        <label key={filter.key} style={optionLabelStyle}>
          <input
            type="checkbox"
            // The EDITED value (disabledFilters), not the server echo, so the tick shows during the debounce window.
            checked={!disabledFilters.includes(filter.key)}
            disabled={isRefetchingListing}
            data-filter-key={filter.key}
            onChange={() => toggleFilter(filter.key)}
            style={checkboxStyle}
          />
          {t(`quote_builder.${FILTER_LABEL_KEYS[filter.key]}`)}
        </label>
      ))}
    </div>
  );

  // The always-on area-ratio reduction's cap, as an adjustable PERCENT -- the Debug knob for tuning how much
  // bigger than the sheet a printing bed may be before the line is dropped. It reads as the rule itself: "Drop a
  // printing bed more than [20] % larger in area than the sheet." Committed on blur / Enter (commitAreaRatio),
  // which re-lists and re-scores against the new cap.
  const renderAreaRatioControl = () => (
    <label style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      Drop a printing bed more than
      <input
        type="text"
        inputMode="numeric"
        data-area-ratio-percent
        value={areaRatioInput}
        onChange={(event) => setAreaRatioInput(event.target.value)}
        onBlur={commitAreaRatio}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitAreaRatio();
          }
        }}
        style={{
          width: '56px', padding: '2px 6px', fontSize: '12px', textAlign: 'right',
          border: '1px solid #d1d5db', borderRadius: '4px'
        }}
      />
      % larger in area than the sheet
    </label>
  );

  // A machine is drawn ONCE per list card. The lines of a list routinely share a machine (one press running
  // both the Cover and the Tray), and drawing it per line reads as though the plan needs two of them. So walk
  // the lines in order and, for a machine already drawn above, leave its column EMPTY rather than repeat it:
  // the empty slot IS the signal -- an empty printing space on the lower line means it prints on the machine
  // shown above it.
  //
  // ONE rule for every part_type, never an `if carton`: an offset pair's press and die-cutter and a flexo
  // line's single combined machine are all just machines. One Set holds them all -- every machine is a
  // UserUnitPriceAttribute row (keyed printing_machine / die_cutting_machine / printing_and_die_cutting_machine),
  // so the ids are unique across the kinds and a press can never collide with a die-cutter.
  const columnVisibilityFor = (manufacturingLines) => {
    // machineId -> the INDEX of the manufacturing line that drew it. Which one drew it matters, not merely
    // that one did: a manufacturing line with no box left is badged onto the line that draws its machines, and
    // only that line can honestly say "this part runs on these machines".
    const drawnBy = new Map();

    return manufacturingLines.map((manufacturingLine, index) => {
      const machineIds = [
        manufacturingLine.printing_machine_id,
        manufacturingLine.die_cutting_machine_id,
        manufacturingLine.printing_and_die_cutting_machine_id
      ];
      const alreadyDrawn = (machineId) => machineId != null && drawnBy.has(machineId);
      const ownMachineIds = machineIds.filter((machineId) => machineId != null);

      // The manufacturing lines that drew this one's machines -- undefined for a machine nobody drew yet.
      const owners = new Set(ownMachineIds.map((machineId) => drawnBy.get(machineId)));
      // Droppable ONLY when every machine came from ONE earlier manufacturing line. That line's machines ARE
      // this one's, so it can carry the badge. Machines drawn by two DIFFERENT lines have no honest target
      // (the part runs on a pair no single row shows), and a line nobody drew from still has a box of its
      // own -- both keep their row. A machine-less line has no owners at all, so it keeps its row too.
      const drawnByOneManufacturingLine = owners.size === 1 && !owners.has(undefined);

      const visibility = {
        hidePrintingColumn: alreadyDrawn(manufacturingLine.printing_machine_id),
        hideDieCuttingColumn: alreadyDrawn(manufacturingLine.die_cutting_machine_id),
        hideCombinedColumn: alreadyDrawn(manufacturingLine.printing_and_die_cutting_machine_id),
        drawsNothing: drawnByOneManufacturingLine,
        // The manufacturing line whose badges this one joins, or null when it keeps its own row.
        badgeOntoIndex: drawnByOneManufacturingLine ? [...owners][0] : null
      };

      // FIRST drawer wins: it is the row the machine is actually drawn on.
      machineIds.forEach((machineId) => {
        if (machineId != null && !drawnBy.has(machineId)) { drawnBy.set(machineId, index); }
      });
      return visibility;
    });
  };

  // One ManufacturingLineList as a single card: a complete way to manufacture the section. A list covering two
  // sub_sections stacks BOTH its manufacturing lines in the one card -- so a card is the familiar machine card,
  // with more machines in it. Each line is badged with the sub_section it makes, since a card holding two
  // identical-looking machine pairs is otherwise unreadable.
  //
  // The card decides which columns each manufacturing line draws, because it is the only place that knows the
  // list's OTHER manufacturing lines -- a ManufacturingLineCard renders the one it is handed.
  //
  // A manufacturing line whose machines were ALL drawn by ONE line above has no box left, so it is not given a
  // row of its own: an empty row under a lone badge reads as unfinished. Its part is badged onto THAT line --
  // the one whose machines are its machines -- so the card reads "Kapak and Kutu, on this pair".
  // alone: the plan is the only card on screen (the picked view), so it is not one of a 4-across grid and must
  // not be squeezed to a quarter width.
  // readOnly: the priced view, where the card names the plan the prices are about rather than offering a pick.
  // Choosing again there goes through the "choose a different solution" button, which de-selects server-side
  // too -- a card that silently un-picked the plan under a priced section would leave the two disagreeing.
  const renderManufacturingLineList = (list, { alone = false, readOnly = false } = {}) => {
    const columnVisibility = columnVisibilityFor(list.manufacturing_lines);
    const drawnManufacturingLines = list.manufacturing_lines
      .map((manufacturingLine, index) => ({ manufacturingLine, index }))
      .filter(({ index }) => !columnVisibility[index].drawsNothing);

    // This manufacturing line's own part, plus the parts of any whose machines IT draws. Badging onto the
    // owner rather than the first row is what keeps the card honest once a list has more than two lines: with
    // three, the machines of the third can belong to the second, and saying it runs on the first's pair would
    // name machines it never touches.
    const sectionLabelsFor = (index) => [
      list.manufacturing_lines[index].sub_section_label,
      ...list.manufacturing_lines
        .filter((_manufacturingLine, otherIndex) => columnVisibility[otherIndex].badgeOntoIndex === index)
        .map((manufacturingLine) => manufacturingLine.sub_section_label)
    ];

    const picked = pickedList === list.identifier;
    // On the Manufacturing Line tab, the SELECTED line is the pick, or -- on a reopened priced section that
    // never held a pick -- the plan its chosen solution is priced from. Either way that line is highlighted and
    // the rest are dimmed, so "this is the selected manufacturing line" reads at a glance.
    const isSelectedLine = list.identifier === (pickedList || pricedListIdentifier);
    // The priced view's card IS the plan in question, so it carries the picked highlight whether or not the
    // page still holds a pick -- a link straight into a priced section never had one.
    const highlighted = picked || readOnly || (stageView === 'line' && isSelectedLine);
    // The plan's best (cheapest) way to make the whole section -- the sum of its lines' bests. Shown once every
    // line's score has landed; '—' when the plan is unmanufacturable (a line with no solution). Its star rating
    // is the score's position in the range on screen: best = 5 stars, worst = 1 (linear min-max, matching the
    // solution cards).
    const scored = listScored(list);
    const listBest = scored ? listBestScore(list) : null;
    const rating = bestScoresReady ? starRatingForScore(listBest, minListScore, maxListScore) : null;

    // Picking toggles the plan. On a USER pick (not a restore from the URL) tell the parent, so the page's
    // Manufacturing Line tab can auto-advance to the Solution tab.
    const pickThisPlan = () => {
      const next = picked ? null : list.identifier;
      setPickedList(next);
      if (onLinePicked && next) { onLinePicked(next); }
    };
    // Fade the un-selected plans once one is selected, so the selected manufacturing line stands out on the
    // page's Manufacturing Line tab.
    const dimmed = stageView === 'line' && Boolean(pickedList || pricedListIdentifier) && !isSelectedLine;

    return (
      <div
        key={list.identifier}
        className={styles.combinations_grid_item_cont}
        data-manufacturing-line-list={list.identifier}
        // Picking a plan asks "how would THIS plan make the section?" -- the solve, which the listing
        // deliberately does not do. Clicking the picked one again goes back to the listing.
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        onClick={readOnly ? undefined : pickThisPlan}
        onKeyDown={readOnly ? undefined : (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            pickThisPlan();
          }
        }}
        style={{
          display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
          flex: alone ? '0 0 auto' : `0 0 ${inWizard ? WIZARD_CARD_BASIS : 'calc(25% - 16px)'}`,
          maxWidth: alone ? '360px' : (inWizard ? WIZARD_CARD_BASIS : 'calc(25% - 16px)'),
          margin: '8px', padding: '12px 8px', cursor: readOnly ? 'default' : 'pointer',
          border: highlighted ? '2px solid var(--brand, #eb5424)' : '1px solid transparent',
          borderRadius: '6px',
          opacity: dimmed ? 0.8 : 1, transition: 'opacity 120ms ease'
        }}
      >
        {/* THE SCORE SLOT, always the same height so a card never jumps when its number lands. Each row fills
            in as soon as what it needs is known: the VALUE the moment this plan's own lines have answered, the
            STARS only once every plan on screen has, because a rating is a position in that range. A plan that
            answered "cannot be made" shows an em dash -- it is answered, not still loading.
            DROPPED on the read-only priced card when it has no score: after a refresh that card is drawn from the
            plan recovered by pricedPlanData, which the best-score pass (it runs over the listing, and the listing
            no longer holds the plan) never scores -- so the slot would sit as a permanent skeleton. The card is
            about WHICH plan was priced, and the machines below carry that, so it simply drops the score row. */}
        {(!readOnly || scored) && (
        <div
          data-best-score={listBest == null ? '' : listBest}
          style={{ textAlign: 'center', marginBottom: '6px' }}
        >
          <div style={{
            marginBottom: '2px', minHeight: '16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          >
            {bestScoresReady
              ? (rating != null ? <Stars rating={rating} /> : null)
              : <div className={`${skeletonStyles.skeleton} ${skeletonStyles.scoreSkeletonStars}`} />}
          </div>
          {scored ? (
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: listBest == null ? '#c00' : '#2e7d32' }}>
              {listBest == null ? '—' : `Skor: ${listBest.toLocaleString()}`}
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className={`${skeletonStyles.skeleton} ${skeletonStyles.scoreSkeletonValue}`} />
            </div>
          )}
        </div>
        )}
        {drawnManufacturingLines.map(({ manufacturingLine, index }) => (
          <ManufacturingLineCard
            key={`${manufacturingLine.identifier}-${index}`}
            manufacturingLine={manufacturingLine}
            maxMachineDimensionMm={maxMachineDimensionMm}
            sectionLabels={sectionLabelsFor(index)}
            bare
            hidePricedSolutionCount
            hidePrintingColumn={columnVisibility[index].hidePrintingColumn}
            hideDieCuttingColumn={columnVisibility[index].hideDieCuttingColumn}
            hideCombinedColumn={columnVisibility[index].hideCombinedColumn}
            widthStyle={{ width: '100%', maxWidth: 'none', margin: 0 }}
          />
        ))}
        {/* An explicit "Select" button, so it is obvious the whole card is a choice -- the card is clickable, but
            that affordance is easy to miss. It does the same pickThisPlan as clicking the card; stopPropagation
            keeps the card's own onClick from ALSO firing (which would toggle the pick right back). Frank surfaces
            only (they set stageView, so t() resolves); not on the read-only priced card or the picked-plan
            header (both `alone`). */}
        {stageView && !readOnly && !alone && (
          <button
            type="button"
            data-frank-select-manufacturing-line={list.identifier}
            className={`dct-button dct-button--small dct-button--block ${isSelectedLine ? 'dct-button--primary' : 'dct-button--secondary'}`}
            style={{ marginTop: '10px' }}
            onClick={(event) => { event.stopPropagation(); pickThisPlan(); }}
          >
            {isSelectedLine ? t('quote_builder.frank_selected') : t('quote_builder.frank_select')}
          </button>
        )}
      </div>
    );
  };

  // The control that goes back to the listing. The picked plan itself stays on screen as its own card above the
  // solutions, so this is only the way back.
  const renderGoBack = () => (
    <button
      type="button"
      data-frank-go-back
      onClick={() => setPickedList(null)}
      className="btn simple-btn"
      style={{ marginBottom: '12px' }}
    >
      Go Back
    </button>
  );

  // The picked plan's SOLUTION GROUPS -- one CARD per group, each holding one solution per part.
  //
  // A plan makes the WHOLE section, and may need two manufacturing lines to do it ("Kapak on the KBA + BOBST,
  // Kutu on the 70x100 Pedal"). Those are two halves of ONE answer, so one card carries both: the legacy's
  // LayoutNavSectionCombinationListList takes a `list` of solutions and draws them all inside a single grid
  // item, which is exactly what a group is (see #buildSolutionGroups for how they are combined).
  //
  // A plan with NO groups is a real answer, not a blank: its machines can be listed for the section's sheets
  // and still fold nothing both beds can run -- and if ONE part cannot be made, the plan cannot make the
  // section at all. So it says so, rather than showing an empty page that reads as broken.
  const renderSolutions = () => {
    // THE SOLVE, in place: the picked plan's card stays on screen above this and the grid area shows the work
    // instead of a full-screen card over the whole page. It draws a fixed few placeholders and an INDETERMINATE
    // bar -- the solve is one request, so neither how many cards are coming nor how far along it is can be
    // known (see SolutionCardsSkeleton).
    if (solutionsLoading) { return <SolutionCardsSkeleton />; }

    const solvedGroups = solutionGroups || [];
    // In a stage view (the wizard's Solution stage or the page's Solution tab) a click just MARKS the card --
    // the marked one is outlined, and it is committed later (wizard Done / page Pricing tab). onSolutionPicked
    // lets the page tabs auto-advance (no-op in the wizard, which has its own Next). On the legacy stacked view
    // (no stageView) a click chooses the solution and prices the section straight away (setSelectedSolutions).
    const useMark = stageView === 'solution';
    const onCardClick = useMark
      ? (group) => { markSolution(group); if (onSolutionPicked) { onSolutionPicked(group); } }
      : setSelectedSolutions;
    // FALLBACK for a reopened PRICED section: the fresh solve can return nothing when the priced plan's machines
    // are off by default (so the default-filtered listing drops it). Rather than tell the user "no solutions"
    // for a section they clearly priced, show the priced solution itself. Only when nothing new is being marked,
    // so an in-session choice always wins.
    const showPricedFallback = useMark && solvedGroups.length === 0
      && !hasSelection(markedSolution) && hasSelection(selectedSolutions);
    const groups = showPricedFallback ? [selectedSolutions] : solvedGroups;
    // The SELECTED solution to emphasise on the Solution tab: the mark, or -- on a reopened priced section that
    // holds no in-session mark -- the priced one. Its card is outlined and the rest are dimmed, so the chosen
    // solution stands out (mirrors the Manufacturing Line tab).
    const markedUuids = useMark ? groupUuids(markedSolution || selectedSolutions) : '';
    // i18n on the customer-facing stage views (wizard + page tabs); the legacy stacked view keeps its exact
    // English, which its jest suite asserts on (t() returns the key there).
    const translated = stageView != null;

    return (
      <div data-frank-solutions={pickedList}>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', textAlign: 'center' }}>
          {translated
            ? t('quote_builder.frank_solutions_heading', { count: groups.length })
            : <>Solutions ({groups.length}) — how this plan makes the section</>}
        </div>

        {groups.length === 0 ? (
          <div style={{ textAlign: 'center', fontSize: '12px', color: '#c00', padding: '8px' }}>
            {translated
              ? t('quote_builder.frank_plan_no_solutions')
              : <>This plan produces no solutions: one of its parts cannot be made on the machine it is assigned to.</>}
          </div>
        ) : null}

        {/* THE LEGACY'S OWN SOLUTION VIEWS, reused verbatim -- the same reuse ManufacturingLineCard already
            gets, and the same distinction: legacy COMPONENTS are fine, legacy SERVICES are not
            (frank.contract, THE FRONT DOOR). A solution reads identically on both pages BY CONSTRUCTION rather
            than by a Frank copy that drifts from it.

            It works because Frank's Frank::DieCuttingSolution#to_hash is deliberately the legacy's shape, so
            the props map straight onto it: diecutting_result, nav_section_id, count, and
            material_layout_dimension as layout_dimension. That was the point of keeping the hash identical. */}
        <div className={styles.combinations_grid} ref={solutionsGridRef}>
          {groups.map((group) => (
            <LayoutNavSectionCombinationListList
              key={groupUuids(group)}
              list={group}
              count={group[0].count}
              setSelectedList={onCardClick}
              selected={markedUuids !== '' && groupUuids(group) === markedUuids}
              dimmed={markedUuids !== '' && groupUuids(group) !== markedUuids}
              columnBasis={inWizard ? WIZARD_CARD_BASIS : null}
              // An explicit "Select" button under each solution card, on the Frank surfaces (the wizard's
              // Solution stage + the page's Solution tab), so the card's clickability is obvious.
              selectButton={translated}
              selectLabel={t('quote_builder.frank_select')}
              selectedLabel={t('quote_builder.frank_selected')}
            />
          ))}
        </div>
      </div>
    );
  };

  // THE LISTING: every complete way to manufacture the section. Shown only while nothing is picked. Sorted
  // best-first once every line's score has landed (listing order until then -- the progress overlay covers it).
  const renderListing = () => {
    // In the WIZARD / a section Manufacturing Line tab, hold the cards until every line has answered and show the
    // scoring progress inline instead: a page of blank-scored cards reads as broken, and the page's full-screen
    // ListingProgressOverlay would sit behind the modal. Only while there ARE lines to score -- with none
    // (e.g. every printing machine unticked) bestScoresReady never turns true, which would leave the progress bar
    // stuck; instead fall through to the empty listing below. The normal page is unchanged (stageView undefined).
    const scoringInProgress = stageView === 'line' && manufacturingLineLists.length > 0 && !bestScoresReady;
    if (scoringInProgress) { return <WizardScoringProgress />; }

    // Nothing matches the current filters (e.g. no printing machine selected). Say so rather than showing an
    // empty "(0)" list.
    if (stageView && manufacturingLineLists.length === 0) {
      return (
        <div style={{ textAlign: 'center', color: 'var(--ink-soft, #4e5865)', fontSize: '14px', padding: '48px 20px' }}>
          {t('quote_builder.frank_no_manufacturing_lines')}
        </div>
      );
    }

    const sortedLists = bestScoresReady
      ? [...manufacturingLineLists].sort((a, b) => {
        const scoreA = listBestScore(a);
        const scoreB = listBestScore(b);
        if (scoreA == null) { return scoreB == null ? 0 : 1; } // unmanufacturable plans sort last
        if (scoreB == null) { return -1; }
        return scoreA - scoreB; // lower score is better -- it is a cost
      })
      : manufacturingLineLists;

    return (
      <>
        {/* i18n only on the customer-facing WIZARD surface (see the note in renderSolutions); the admin/normal
            page keeps its exact English, which its test suite asserts on. */}
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px', textAlign: 'center' }}>
          {stageView
            ? t('quote_builder.frank_manufacturing_line_lists_heading', {
              count: manufacturingLineLists.length,
              sorted: bestScoresReady ? t('quote_builder.frank_sorted_by_best_score') : ''
            })
            : (
              <>
                Manufacturing Line Lists ({manufacturingLineLists.length}) — every complete way to manufacture this
                section{bestScoresReady ? ', sorted by best score' : ''}. Click one to see its solutions.
              </>
            )}
        </div>
        {/* The listing's progress while its manufacturing lines are scored: the modal, blocking popup with a
            short "Loading…" title and a determinate percentage bar, over the whole page. It clears itself once
            every line has answered. The wizard shows its own inline bar above instead. */}
        {!stageView && <ListingProgressOverlay />}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
          {sortedLists.map((list) => renderManufacturingLineList(list))}
        </div>
      </>
    );
  };

  // THE PICKED VIEW: the plan on its own, centred, and its solutions under it -- the same shape the legacy page
  // uses (LayoutNavSectionCombinationList: Go Back, the selected card centred, an <hr />, then the solutions).
  //
  // It REPLACES the listing rather than appending to it. Appending is what the first cut did, and with 237 plan
  // cards above them the solutions landed several screens down: clicking a card looked like nothing happened,
  // even though the solve had run and the cards had rendered. A DOM assertion could not see that -- only opening
  // the page could.
  const renderPickedPlan = () => {
    const picked = manufacturingLineLists.find((list) => list.identifier === pickedList);

    return (
      <>
        {renderGoBack()}
        {picked ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {renderManufacturingLineList(picked, { alone: true })}
          </div>
        ) : null}
        <hr />
        {renderSolutions()}
      </>
    );
  };

  // THE CHOSEN SOLUTION, and the section priced from it. It REPLACES the listing and the solutions, the way the
  // legacy page does: once a solution is chosen the question is no longer which one, and leaving the whole
  // listing above the prices buries them (the same reason the picked plan replaces the listing).
  // THE PAGE'S PRICING TAB. Once a solution is chosen it is the priced view (renderSelectedSolution). Arriving
  // here with a solution only MARKED (from the Solution tab) is what commits it -- the pricing commit effect
  // turns the mark into a selection, which posts select-solution and prices the section -- so this shows a short
  // "pricing" note while that is in flight. With nothing marked or chosen it points the user back to the
  // Solution tab.
  const renderPricing = () => {
    if (hasSelection(selectedSolutions)) { return renderSelectedSolution(); }
    const message = hasSelection(markedSolution)
      ? t('quote_builder.frank_pricing_in_progress')
      : t('quote_builder.frank_pricing_needs_solution');
    return (
      <div style={{ textAlign: 'center', color: 'var(--ink-soft, #4e5865)', fontSize: '14px', padding: '48px 20px' }}>
        {message}
      </div>
    );
  };

  const renderSelectedSolution = () => {
    // The plan the section is priced from -- the very card the viewer picked. Taken from
    // effectiveManufacturingLineLists so it is found both in-session (from the listing) and after a refresh (from
    // pricedPlanData, fetched unfiltered because the default-filtered listing no longer holds it). Still absent
    // for a selection made on the legacy page, which stamps no manufacturing line identifier on its cards; the
    // prices are then about the chosen solution regardless, so the plan card is simply left out.
    const pricedPlan = planOnScreen
      ? effectiveManufacturingLineLists.find((list) => list.identifier === planOnScreen)
      : null;

    // On the page's PRICING tab the plan and the chosen solution have their own tabs (Manufacturing Line /
    // Solution), so this view shows ONLY the prices: the "choose a different solution" button and the plan +
    // solution cards below are hidden (the solution card stays mounted but display:none, because it writes the
    // num_total_* totals the cost forms read -- removing it would leave those columns at 0). The legacy stacked
    // view (no stageView) keeps them: it is the whole flow in one column.
    const pricingTab = stageView === 'pricing';

    return (
      <>
        {!pricingTab && (
        <button
          type="button"
          data-frank-choose-another-solution
          // Through the SHARED reset handler, so the server forgets the selection too. Clearing only the page's
          // state left user_quote_nav_sections.selected_lists and the quote's builder_payload still holding the
          // rejected solution: a refresh reopened the section priced from it, and the Manage tab's totals went on
          // counting it. It also resets the linked sections and reloads when the server says to, which the legacy
          // section's own Back button has always done -- this page had a second button that did none of it.
          onClick={() => resetSelectedListHandler({
            index: navSectionIndex,
            setSelectedList: setSelectedSolutions
          })}
          style={{
            marginBottom: '12px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer',
            border: '1px solid var(--rule, #e3e7ec)', borderRadius: 'var(--dct-radius, 8px)',
            background: 'var(--surface, #ffffff)', color: 'var(--ink-soft, #4e5865)'
          }}
        >
          ← {t('quote_builder.choose_a_different_solution')}
        </button>
        )}

        {/* WHAT THE PRICES ARE ABOUT, kept above them: the plan the section is manufactured on, and the solution
            card that was chosen off it -- the two cards the viewer clicked through to get here. Without them the
            priced view is a page of forms with no subject, and the only way to see which plan and which solution
            it was pricing was to go back, which throws the selection away.

            Both are the cards the rest of the page already draws, not a summary written again here: the plan
            through renderManufacturingLineList (the listing's own card), the solution through the legacy
            LayoutNavSectionCombinationListList (the same card the solutions grid shows). So they read identically
            to the ones that were clicked.

            READ-ONLY, both of them: choosing again is the button above, which de-selects on the server too. */}
        <div
          data-frank-priced-from
          style={{
            // Hidden on the Pricing tab (its plan + solution live on the other two tabs) but kept mounted, so
            // the solution card below still writes the num_total_* totals the cost forms read.
            display: pricingTab ? 'none' : 'flex', flexWrap: 'wrap', justifyContent: 'center',
            alignItems: 'flex-start', marginBottom: '12px'
          }}
        >
          {pricedPlan ? renderManufacturingLineList(pricedPlan, { alone: true, readOnly: true }) : null}
          <LayoutNavSectionCombinationListList
            // THE SELECTION ITSELF, deliberately not a copy. The shared card writes num_total_diecutting /
            // num_total_printing / num_of_drawings_in_set back into whatever `list` it is handed once its score
            // lands, and that write is LOAD-BEARING: SolutionDimensions in the table below reads those totals
            // off the solution, so a copy leaves both columns reading "(0)".
            //
            // What made that mutation dangerous was never the mutation -- it was that a stale selection could
            // be visible under the WRONG section index for one render, which turned the drift into a post
            // against another section. The selection is tagged with its section now, so it cannot be.
            list={selectedSolutions}
            count={selectedSolutions[0].count}
            // A no-op: the card is here to say WHICH solution is priced, not to choose one. The shared card
            // always wires its container to setSelectedList, so this is where the click is dropped.
            setSelectedList={() => {}}
          />
        </div>

        <SelectedSolutionSections
          config={config}
          navSectionIndex={navSectionIndex}
          selectedSolutions={selectedSolutions}
          setSelectedSolutions={setSelectedSolutions}
          hideBackButton={pricingTab}
        />
      </>
    );
  };

  return (
    <ProgressProvider>
      <div style={{ marginTop: '10px', marginBottom: '10px', position: 'relative' }}>

      {/* Asked ONCE for the whole section, above the sub_sections they all narrow. Hidden once a plan is picked
          or a solution chosen: the filters narrow the LISTING, and the listing is not what is on screen then.
          While a change re-lists, the whole block is dimmed and its clicks blocked (pointerEvents), on top of
          each input being disabled -- so no filter can be touched until the results are back. */}
      {stageView === 'line' || (!stageView && !pickedList && !hasSelection(selectedSolutions)) ? (
        <div
          data-section-machine-filters
          aria-busy={isRefetchingListing}
          style={{
            marginBottom: '12px', opacity: isRefetchingListing ? 0.55 : 1,
            pointerEvents: isRefetchingListing ? 'none' : 'auto', transition: 'opacity 120ms ease'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {renderSectionFilters()}
          </div>
          {adminDebugVisible && <DebugPanel title="Filters">{renderFilterToggles()}</DebugPanel>}
          {adminDebugVisible && <DebugPanel title="Area ratio">{renderAreaRatioControl()}</DebugPanel>}
        </div>
      ) : null}

      {(() => {
        // A stage view (wizard stage or a page section tab) renders ONE sub-view; the wizard / the page tabs
        // drive the progression between them.
        if (stageView === 'line') { return renderListing(); }
        if (stageView === 'solution') { return renderSolutions(); }
        if (stageView === 'pricing') { return renderPricing(); }

        // The legacy stacked view: the whole flow in one column.
        if (hasSelection(selectedSolutions)) { return renderSelectedSolution(); }

        return pickedList ? renderPickedPlan() : renderListing();
      })()}

      {/* The sub_sections themselves are NOT listed: each is the working behind the lists, not an answer --
          "Kapak" on its own makes no shirt box, it makes a cover. Every one of its manufacturing lines already
          appears above, inside the lists that use it. */}
      </div>
    </ProgressProvider>
  );
};

export default ManufacturingLineLists;
